/**
 * "Is there anything to release?", turned into the sentence the idle home page
 * shows and the decision it makes. Pure and React-free so it is unit-tested.
 *
 * The page is modal on the answer (DESIGN §10): with nothing unreleased there is
 * nothing to prepare, so the start action is not offered at all — offering it
 * invites preparing an empty release, and a disabled button that explains itself
 * only after a hover is not much better. What is offered instead is the way to
 * get a current answer: a sync. (Waking the system is handled before this card
 * renders — the whole flow is gated on it — so the check is always something that
 * can run now.)
 *
 * The answer comes from `main/unreleased`, which only `host.sync` on the prepare
 * host writes, so it is a photograph rather than a live feed. Two of the four
 * states exist to say so honestly — there is no check yet, or the checkout has
 * moved since the one there is.
 *
 * "Released" throughout means `deployed/server`, the tag the deploy side moves to
 * what it shipped — the same baseline the change set and the deploy plan use. The
 * copy here says so, because an operator comparing this card against the release
 * tag would reach a different number: a deploy ships the release branch tip,
 * which can sit well above the tag the release was cut at.
 */
import { componentReleaseSummary, type HostDoc, type UnreleasedDoc } from '@finvari/deployer';
import { agoTextCoarse } from './hostStatus';

export type UnreleasedState =
    /** No check has ever run — nothing has been published for this checkout. */
    | 'unchecked'
    /** The release branch has moved since the check, so the answer is about a state that is gone. */
    | 'stale'
    /** Checked: everything on main is already deployed. */
    | 'clean'
    /** Checked: there is work on main, or on the release branch, that is not deployed. */
    | 'changes';

export interface UnreleasedView {
    state: UnreleasedState;
    /** The one-line answer, for the card heading. */
    headline: string;
    /** Why that is the answer, and what the operator can do about it. */
    detail: string;
    /** Whether preparing a release is worth offering. */
    canStart: boolean;
    /** The components the unreleased diff touches, biggest first; empty unless `changes`. */
    components: UnreleasedDoc['components'];
    /**
     * One sentence per connector or app release a start would also announce, or
     * empty. Surfaced on the idle page because it changes what the operator is
     * about to do: a release with a connector build in it needs that build's
     * installer deployed and its own announcement reviewed, and finding that out
     * at the review gate is late.
     */
    componentReleases: string[];
    /**
     * One sentence per Pulumi program the waiting work changes, or empty. Here for
     * the same reason as `componentReleases` and a sharper one: nothing a release
     * does applies a program, so this is work the release will hand back to an
     * owner. Knowing before the release starts is the difference between arranging
     * a `pulumi up` and discovering at the close that one is needed.
     */
    infraPrograms: string[];
    /** How long ago the check ran, when there is one. */
    checkedAgo?: string;
    /** The version a release started now would produce; only when there is something to release. */
    nextVersion?: string;
}

const CHECK_HINT = 'Sync the prepare host to check.';

function plural(n: number, one: string): string {
    return n === 1 ? '1 ' + one : n + ' ' + one + 's';
}

/**
 * The idle page's reading of the last check. `prepareHost` is what makes
 * staleness visible: the heartbeat reports the checkout's HEAD, and a check
 * describes the sha it was taken at, so the two disagreeing means the branch
 * moved (a preparation ran, or someone reset it over SSH) and the counts below
 * are about a state the host is no longer in.
 */
export function unreleasedView(
    doc: UnreleasedDoc | undefined,
    prepareHost: HostDoc | undefined,
    now: number,
): UnreleasedView {
    if (!doc) {
        return {
            state: 'unchecked',
            headline: 'Nothing checked yet',
            detail: `Nobody has asked what is waiting to be released on this checkout. ${CHECK_HINT}`,
            canStart: false,
            components: [],
            componentReleases: [],
            infraPrograms: [],
        };
    }

    const checkedAgo = doc.checkedAt?.toMillis ? agoTextCoarse(Math.max(0, now - doc.checkedAt.toMillis())) : undefined;
    const asOf = checkedAgo ? ' (checked ' + checkedAgo + ')' : '';
    const componentReleases = (doc.componentReleases ?? []).map(componentReleaseSummary);
    const infraPrograms = (doc.infraPrograms ?? []).map(
        (p) => `${p.program} (${p.name}) — ${plural(p.fileCount, 'file')}, needing a pulumi up by an owner.`,
    );

    if (prepareHost?.checkoutHead && prepareHost.checkoutHead !== doc.releaseSha) {
        return {
            state: 'stale',
            headline: 'The last check is out of date',
            detail: `The release branch has moved since it was checked${asOf}, so that answer no longer describes it. ${CHECK_HINT}`,
            canStart: false,
            components: [],
            componentReleases: [],
            infraPrograms: [],
            checkedAgo,
        };
    }

    if (doc.commits === 0) {
        // Nothing on main is unshipped, but the release branch may still hold work
        // that never went out (a fix pushed straight to release, or a cycle that
        // merged but never deployed). Only the branch comparison can see that, so
        // the start action must still be offered — a guard the laptop release tool
        // carried before this one, against a "false fully-released" reading.
        if (doc.releaseAheadOfDeployed) {
            return {
                state: 'changes',
                headline: `${doc.nextVersion} not yet released`,
                detail: `Nothing on main is unreleased, but the release branch holds commits that deployed/server does not${asOf} — work that was never shipped. Preparing ${doc.nextVersion} will release it.`,
                canStart: true,
                components: [],
                componentReleases,
                infraPrograms,
                checkedAgo,
                nextVersion: doc.nextVersion,
            };
        }
        return {
            state: 'clean',
            headline: 'Nothing to release',
            detail: `deployed/server already covers every commit on main${asOf}, so everything written is in production. Preparing a release is offered here once main is ahead of what is deployed.`,
            canStart: false,
            components: [],
            componentReleases: [],
            infraPrograms: [],
            checkedAgo,
        };
    }

    const components = [...doc.components].sort((a, b) => b.fileCount - a.fileCount);
    // The component count is the compile set, which is the honest answer to "how
    // much does this release touch". The deploy count is the more useful one, and
    // it is only worth a clause when the two differ.
    const compileOnly = components.filter((c) => c.changeKind === 'compile-only').length;
    const deploying = compileOnly === 0 ? '' : ` — ${components.length - compileOnly} of them needing a deploy`;
    return {
        state: 'changes',
        headline: `${plural(doc.commits, 'commit')} waiting for ${doc.nextVersion}`,
        detail: `Main is ${plural(doc.commits, 'commit')} ahead of what is deployed, changing ${plural(doc.fileCount, 'file')} across ${plural(components.length, 'component')}${deploying}${asOf}.`,
        canStart: true,
        components,
        componentReleases,
        infraPrograms,
        checkedAgo,
        nextVersion: doc.nextVersion,
    };
}
